<script setup lang="ts">
    import { onMounted, onUnmounted, ref, watch } from 'vue';
    import Plotly from 'plotly.js-basic-dist';
    import { type NomogramData } from '@/domain/anatomy/pelvis/hipSpineNomogram';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';
    import { resolveThemeColor } from '@/util/resolveThemeColor';

    const props = defineProps<{
        nomogramData: NomogramData;
    }>();

    const plotRef = ref<HTMLElement | null>(null);
    const chartContainer = ref<HTMLElement | null>(null);
    let resizeObserver: ResizeObserver | null = null;

    function buildTraces(): Partial<Plotly.Data>[] {
        const traces: Partial<Plotly.Data>[] = [];
        const ranges = props.nomogramData.angleRanges;

        if (!ranges) return traces;

        const angleRangeFill = resolveThemeColor('--v-theme-grey-light-1', 0.5);
        const safeZoneFill = resolveThemeColor('--v-theme-green', 1);
        const combinedVersionColor = resolveThemeColor('--v-theme-primary', 1);
        const targetLineColor = resolveThemeColor('--v-theme-secondary-text', 1);

        // Inclination angle range — vertical band spanning full Y axis
        traces.push({
            x: [
                ranges.minInclination,
                ranges.maxInclination,
                ranges.maxInclination,
                ranges.minInclination,
                ranges.minInclination,
            ],
            y: [
                MIN_ANTEVERSION,
                MIN_ANTEVERSION,
                MAX_ANTEVERSION,
                MAX_ANTEVERSION,
                MIN_ANTEVERSION,
            ],
            mode: 'lines',
            fill: 'toself',
            fillcolor: angleRangeFill,
            line: { color: 'none', width: 0 },
            name: 'Inclination Range',
            hoverinfo: 'name',
            showlegend: false,
        });

        // Anteversion angle range — horizontal band spanning full X axis
        traces.push({
            x: [
                MIN_INCLINATION,
                MAX_INCLINATION,
                MAX_INCLINATION,
                MIN_INCLINATION,
                MIN_INCLINATION,
            ],
            y: [
                ranges.minAnteversion,
                ranges.minAnteversion,
                ranges.maxAnteversion,
                ranges.maxAnteversion,
                ranges.minAnteversion,
            ],
            mode: 'lines',
            fill: 'toself',
            fillcolor: angleRangeFill,
            line: { color: 'none', width: 0 },
            name: 'Anteversion Range',
            hoverinfo: 'name',
            showlegend: false,
        });

        // Safe zone — the intersection of angle range rectangle and CSI bounds
        const safeZonePoly = props.nomogramData.safeZonePolygon;
        if (safeZonePoly) {
            traces.push({
                x: safeZonePoly.inclination,
                y: safeZonePoly.anteversion,
                mode: 'lines',
                fill: 'toself',
                fillcolor: safeZoneFill,
                line: { color: 'none', width: 0 },
                name: 'Safe Zone',
                hoverinfo: 'name',
                showlegend: false,
            });
        }

        // Combined version target — curve transformed to the active alignment mode
        if (props.nomogramData.combinedVersionTarget) {
            const cvt = props.nomogramData.combinedVersionTarget;
            traces.push({
                x: cvt.inclination,
                y: cvt.anteversion,
                mode: 'lines',
                line: { color: combinedVersionColor, width: 2, dash: 'solid' },
                name: 'Combined Version Target',
                hovertemplate: 'Combined Version Target<extra></extra>',
                showlegend: false,
            });
        }

        // CSI bound lines
        const csiBoundColor = resolveThemeColor('--v-theme-grey-1', 1);

        // Upper CSI bound (245°)
        traces.push({
            x: props.nomogramData.csiBounds.upper.inclination,
            y: props.nomogramData.csiBounds.upper.anteversion,
            mode: 'lines',
            line: { color: csiBoundColor, width: 2, dash: 'solid' },
            name: 'CSI Upper Bound',
            hovertemplate: 'CSI Upper Bound (245°)<extra></extra>',
            showlegend: false,
        });

        // Lower CSI bound (205°)
        traces.push({
            x: props.nomogramData.csiBounds.lower.inclination,
            y: props.nomogramData.csiBounds.lower.anteversion,
            mode: 'lines',
            line: { color: csiBoundColor, width: 2, dash: 'solid' },
            name: 'CSI Lower Bound',
            hovertemplate: 'CSI Lower Bound (205°)<extra></extra>',
            showlegend: false,
        });

        // Inclination/anteversion target marker crosshair
        if (props.nomogramData.marker) {
            const mx = props.nomogramData.marker.inclination;
            const my = props.nomogramData.marker.anteversion;

            // Vertical line
            traces.push({
                x: [mx, mx],
                y: [MIN_ANTEVERSION, MAX_ANTEVERSION],
                mode: 'lines',
                line: { color: targetLineColor, width: 1, dash: 'dot' },
                name: 'Inclination Target',
                hoverinfo: 'skip',
                showlegend: false,
            });

            // Horizontal line
            traces.push({
                x: [MIN_INCLINATION, MAX_INCLINATION],
                y: [my, my],
                mode: 'lines',
                line: { color: targetLineColor, width: 1, dash: 'dot' },
                name: 'Anteversion Target',
                hoverinfo: 'skip',
                showlegend: false,
            });
        }

        return traces;
    }

    function buildLayout(): Partial<Plotly.Layout> {
        return {
            xaxis: {
                title: { text: 'Inclination (degrees)', font: { size: 12, color: '#666' } },
                range: [MIN_INCLINATION, MAX_INCLINATION],
                dtick: 10,
                zeroline: false,
                linecolor: 'lightgrey',
                tickfont: { size: 11, color: '#888' },
                fixedrange: true,
                mirror: 'ticks',
                ticks: 'outside',
                ticklen: 5,
                tickcolor: 'transparent',
            },
            yaxis: {
                title: { text: 'Anteversion (degrees)', font: { size: 12, color: '#666' } },
                range: [MIN_ANTEVERSION, MAX_ANTEVERSION],
                dtick: 10,
                zeroline: false,
                linecolor: 'lightgrey',
                tickfont: { size: 11, color: '#888' },
                fixedrange: true,
                mirror: 'ticks',
                ticks: 'outside',
                ticklen: 5,
                tickcolor: 'transparent',
            },
            margin: { l: 55, r: 15, t: 15, b: 50 },
            showlegend: false,
            paper_bgcolor: 'transparent',
            plot_bgcolor: 'transparent',
            autosize: true,
            dragmode: false,
            hovermode: 'closest',
        };
    }

    const plotlyConfig: Partial<Plotly.Config> = {
        displayModeBar: false,
        responsive: true,
        staticPlot: false,
        scrollZoom: false,
    };

    function renderChart(): void {
        if (!plotRef.value) return;

        const traces = buildTraces();
        const layout = buildLayout();

        Plotly.react(plotRef.value, traces, layout, plotlyConfig);
    }

    watch(
        () => props.nomogramData,
        () => renderChart(),
        { deep: true },
    );

    onMounted(() => {
        renderChart();

        // Handle container resizes
        if (chartContainer.value) {
            resizeObserver = new ResizeObserver(() => {
                if (plotRef.value) {
                    Plotly.Plots.resize(plotRef.value);
                }
            });
            resizeObserver.observe(chartContainer.value);
        }
    });

    onUnmounted(() => {
        if (plotRef.value) {
            Plotly.purge(plotRef.value);
        }
        resizeObserver?.disconnect();
    });
</script>

<template>
    <div ref="chartContainer" class="nomogram">
        <div v-if="!nomogramData.angleRanges && !nomogramData.marker" class="nomogram-empty">
            <p class="text-secondary-text text-body-2">
                Something went wrong. Please contact support.
            </p>
        </div>
        <div v-else ref="plotRef" class="nomogram__plot" data-testid="hip-spine-nomogram" />
    </div>
</template>

<style scoped lang="scss">
    .nomogram {
        width: 100%;
        min-height: 150px;
        max-height: 300px;
        flex: 1 1 300px;
    }

    .nomogram__plot {
        width: 100%;
        height: 100%;
    }

    .nomogram-empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100px;
        padding: 16px;
    }
</style>
